import { useState, useEffect, useCallback } from 'react';
import type { RegionalPerformanceRow } from '../utils/regionalClientPerformanceUtils';

const PATHS = [
  '/json-data/regional_client_performance.json',
  '/data_models/regional_client_performance.json',
] as const;

interface UseRegionalClientPerformanceResult {
  records: RegionalPerformanceRow[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useRegionalClientPerformance(): UseRegionalClientPerformanceResult {
  const [records, setRecords] = useState<RegionalPerformanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick(t => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      let lastErr: Error | null = null;
      for (const path of PATHS) {
        try {
          const r = await fetch(path);
          if (!r.ok) {
            lastErr = new Error(`${path}: ${r.status}`);
            continue;
          }
          const json = await r.json();
          if (!Array.isArray(json)) {
            lastErr = new Error('Regional performance data must be a JSON array');
            continue;
          }
          if (!cancelled) {
            setRecords(json as RegionalPerformanceRow[]);
            setLoading(false);
          }
          return;
        } catch (e) {
          lastErr = e instanceof Error ? e : new Error(String(e));
        }
      }
      if (!cancelled) {
        setError(lastErr?.message || 'Failed to load regional_client_performance.json');
        setRecords([]);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { records, loading, error, refetch };
}
